import * as React from 'react';
import { sanitizeHtml } from '../../shared/sanitizeHtml';
import {
    Autocomplete,
    Checkbox,
    Chip,
    Button,
    Divider,
    Box,
    FormControlLabel,
    Typography,
    TextField,
    CircularProgress,
    Alert
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { UserRole } from '../../components/common/header/photo/head-bar/newsComponent/utils/userRoleHelper';
import { useTelemetryContext } from '../../telemetry/telemetryContext';
import { getUserInfo } from '../../telemetry/utils';
import { useSelector } from 'react-redux';
import { getUser } from '../../reducers/userReducer';
import { INewsItem } from './NewsItem';
import { getEndDateError, validateNewsForPublish } from './NewsClientValidation';
import { Translate } from '../../shared/LanguageService';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import NewsEditorModalLinkButton from './NewsEditorModalLinkButton';
import { NewsItemStatus } from './NewsUtils';
import NewsPreviewDialog, { NewsPreview } from './preview/NewsPreviewDialog';
import { getScheduleAudience } from './preview/newsPreviewAudience';
import { describeAudience } from './preview/describeAudience';
import { pasteAsTextWithLinks } from './pasteAsTextWithLinks';
import { parseNewsDate, todayNewsDateText, toNewsDateText } from './newsDates';
import { newsBodyTextSx, newsTextWidthPx } from './feed/newsPaneStyle';

export type NewsEditorModalLabels = {
    addNewsTitle: string;
    editNewsTitle: string;
    titleLabel: string;
    contentLabel: string;
    recipientsLabel: string;
    selectRecipientsPlaceholder: string;
    brokerChainsLabel: string;
    brokerChainsPlaceholder: string;
    startDatePicker: string;
    visibleDatePicker: string;
    endDatePicker: string;
    saveDraftButton: string;
    publishNewsButton: string;
    brokerChainsSelectAllLabel: string;
    insertLinkTitle: string;
    insertLinkTextPlaceholder: string;
    insertLinkUrlPlaceholder: string;
    insertLinkButton: string;
    archiveButton: string;
    unarchiveButton: string;
    previewButton: string;
    contentWidthHint: string;
    startDateHint: string;
    visibleDateHint: string;
    endDateHint: string;
    optionalLabel: string;
    scheduleNewsButton: string;
    sectionContent: string;
    sectionRecipients: string;
    sectionSchedule: string;
};

export type BrokerChain = {
    id: number;
    name: string;
};

export type NewsEditorModalFormProps = {
    onClose: () => void;
    roles: { label: string; value: UserRole }[];
    brokerChains: BrokerChain[];
    brokerChainsLoading: boolean;
    onPublish: (item: INewsItem, brokerChainIds: number[]) => Promise<void>;
    onSaveDraft: (item: INewsItem, brokerChainIds: number[]) => Promise<void>;
    editingNews?: INewsItem | null;
    labels: NewsEditorModalLabels;
};

enum FormNames {
    Title = 'news-title',
    Content = 'news-content',
    StartDate = 'news-start-date',
    EndDate = 'news-end-date',
    VisibleDate = 'news-visible-date',
    Recipients = 'news-recipients',
    BrokerChain = 'news-broker-chain',
    Action = '_action',
    Id = 'news-id'
}

enum ButtonAction {
    SaveDraft = 'save-draft',
    Publish = 'publish',
    Archive = 'archive'
}

function getInitialSelectedRoles(
    editingNews: INewsItem | null | undefined,
    roles: { label: string; value: UserRole }[]
): { label: string; value: UserRole }[] {
    const uniqueRoleKeys = Array.from(
        new Set(
            (editingNews && editingNews.recipients ? editingNews.recipients : [])
                .map((r) => r.role)
                .filter(Boolean)
        )
    );

    return uniqueRoleKeys
        .map((roleKey: string) => roles.find((x) => x.value === roleKey))
        .filter(Boolean) as { label: string; value: UserRole }[];
}

function getInitialSelectedBrokerChains(
    editingNews: INewsItem | null | undefined,
    brokerChains: BrokerChain[]
): BrokerChain[] {
    if (!editingNews || !editingNews.recipients) {
        return [];
    }

    const brokerChainIds = Array.from(
        new Set(editingNews.recipients.map((recipient) => recipient.brokerChainId).filter(Boolean))
    );

    return brokerChains.filter((brokerChain) => brokerChainIds.includes(brokerChain.id));
}

function startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

const contentLabelId = 'news-content-label';

// Otherwise Enter presses the first submit button, which is Archive for saved news.
function preventEnterFromSubmittingForm(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') {
        event.preventDefault();
    }
}

function SectionHeading({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            variant="overline"
            component="h3"
            sx={{ fontWeight: 600, color: 'text.secondary', lineHeight: 1.5 }}
        >
            {children}
        </Typography>
    );
}

type NewsDatePickerProps = {
    name: string;
    label: string;
    value: Date | null;
    onChange: (value: Date | null) => void;
    error?: string;
    hint: string;
    onEdit: () => void;
};

function NewsDatePicker({
    name,
    label,
    value,
    onChange,
    error,
    hint,
    onEdit
}: NewsDatePickerProps) {
    return (
        <DesktopDatePicker
            label={label}
            value={value}
            onChange={onChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    id={name}
                    name={name}
                    error={Boolean(error)}
                    helperText={error || hint}
                    fullWidth
                    size="small"
                    onChange={onEdit}
                />
            )}
        />
    );
}

export default function NewsEditorModalForm({
    onClose,
    roles,
    brokerChains,
    brokerChainsLoading,
    labels,
    onSaveDraft,
    onPublish,
    editingNews
}: NewsEditorModalFormProps) {
    const [selectedRoles, setSelectedRoles] = React.useState<{ label: string; value: UserRole }[]>(
        () => getInitialSelectedRoles(editingNews, roles)
    );

    const [startDate, setStartDate] = React.useState<Date | null>(() =>
        editingNews ? parseNewsDate(editingNews.startDate) : startOfToday()
    );

    const [visibleDate, setVisibleDate] = React.useState<Date | null>(() =>
        editingNews ? parseNewsDate(editingNews.visibleDate) : startOfToday()
    );

    const [visibleDateFollowsStartDate, setVisibleDateFollowsStartDate] = React.useState(
        () => !editingNews || !editingNews.visibleDate
    );

    const [endDate, setEndDate] = React.useState<Date | null>(() =>
        parseNewsDate(editingNews && editingNews.endDate)
    );

    const [selectedBrokerChains, setSelectedBrokerChains] = React.useState<BrokerChain[]>(() =>
        getInitialSelectedBrokerChains(editingNews, brokerChains)
    );

    const [formErrors, setFormErrors] = React.useState<{ [key: string]: string }>({});
    const [showSaveError, setShowSaveError] = React.useState(false);

    const refreshEndDateError = (nextStartDate: Date | null, nextEndDate: Date | null) => {
        setFormErrors((prev) => {
            if (!('endDate' in prev)) {
                return prev;
            }

            const newError = getEndDateError(
                toNewsDateText(nextStartDate),
                toNewsDateText(nextEndDate)
            );

            if (newError === prev.endDate) {
                return prev;
            }

            const next = { ...prev };

            if (newError) {
                next.endDate = newError;
            } else {
                delete next.endDate;
            }

            return next;
        });
    };

    function clearError(key: string) {
        setFormErrors((prev) => {
            if (!(key in prev)) {
                return prev;
            }
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }

    const contentEditableRef = React.useRef<HTMLDivElement>(null);
    const formRef = React.useRef<HTMLFormElement>(null);
    const [preview, setPreview] = React.useState<NewsPreview | null>(null);

    function handlePreview() {
        const formData = new FormData(formRef.current || undefined);
        const item = buildNewsItem(formData, false);
        setPreview({
            title: item.title,
            content: item.content,
            recipientRoles: selectedRoles.map((role) => role.value),
            startDate: item.startDate,
            visibleDate: item.visibleDate,
            endDate: item.endDate,
            brokerChainCount: selectedBrokerChains.length
        });
    }

    const handleContentChange = () => {
        if (formErrors.content) {
            setFormErrors((prev) => {
                const next = { ...prev };
                delete next.content;
                return next;
            });
        }
    };

    const isBrokerAgentSelected = selectedRoles.some((role) => role.value === UserRole.BrokerAgent);

    const allBrokerChainsSelected =
        brokerChains.length > 0 && selectedBrokerChains.length === brokerChains.length;

    const handleSelectAllBrokerChains = () => {
        if (allBrokerChainsSelected) {
            setSelectedBrokerChains([]);
        } else {
            setSelectedBrokerChains(brokerChains);
        }
    };

    const startDateText = toNewsDateText(startDate);
    const isScheduled = startDateText !== null && startDateText > todayNewsDateText();
    const scheduleAudience = getScheduleAudience({
        recipientRoles: selectedRoles.map((role) => role.value),
        startDate: startDateText,
        endDate: toNewsDateText(endDate),
        brokerChainCount: selectedBrokerChains.length
    });

    function handleStartDateChange(newValue: Date | null) {
        setStartDate(newValue);
        refreshEndDateError(newValue, endDate);
        if (visibleDateFollowsStartDate) {
            setVisibleDate(newValue);
            clearError('visibleDate');
        }
    }

    let { logger } = useTelemetryContext();
    let user = useSelector((state) => getUser(state));

    function buildNewsItem(formData: FormData, visible: boolean): INewsItem {
        const title = String(formData.get(FormNames.Title) || '').trim();
        const content = contentEditableRef.current
            ? contentEditableRef.current.innerHTML.trim()
            : '';

        return {
            id: editingNews ? editingNews.id : -1,
            title,
            content,
            recipients: selectedRoles.map((r) => ({ role: r.value, brokerChainId: null })),
            startDate: toNewsDateText(startDate),
            visibleDate: toNewsDateText(visibleDate),
            endDate: toNewsDateText(endDate),
            visible
        };
    }

    async function handlePublish(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        // @ts-ignore
        const submitter = event.nativeEvent.submitter as HTMLButtonElement | undefined;
        if (!submitter) {
            return;
        }

        setFormErrors({});

        const formData = new FormData(event.currentTarget);

        const isPublish = submitter.value === ButtonAction.Publish;
        const isSaveDraft = submitter.value === ButtonAction.SaveDraft;
        const isArchive = submitter.value === ButtonAction.Archive;

        const item = buildNewsItem(formData, isPublish);
        const brokerChainIds = selectedBrokerChains.map((x) => x.id);

        if (isArchive) {
            const isCurrentlyArchived =
                editingNews && editingNews.status === NewsItemStatus.Archived;
            item.visible = false;
            item.status = isCurrentlyArchived ? NewsItemStatus.Draft : NewsItemStatus.Archived;
        }

        if (isPublish) {
            const clientErrors = validateNewsForPublish({
                title: item.title,
                content: item.content,
                startDate: item.startDate,
                visibleDate: item.visibleDate,
                endDate: item.endDate,
                recipients: item.recipients,
                brokerChainIds
            });

            if (Object.keys(clientErrors).length > 0) {
                setFormErrors(clientErrors);
                return;
            }
        }

        if (!isSaveDraft && !isPublish && !isArchive) {
            const unknownButtonActionError = new Error(
                `Unknown button value: ${submitter.value}, only ${ButtonAction.SaveDraft}, ${ButtonAction.Publish} and ${ButtonAction.Archive} is supported!`
            );

            logger.logException(unknownButtonActionError, {
                origin: 'NewsEditorModal',
                userInfo: getUserInfo(user)
            });

            throw unknownButtonActionError;
        }

        try {
            if (isArchive || isSaveDraft) {
                await onSaveDraft(item, brokerChainIds);
            } else {
                await onPublish(item, brokerChainIds);
            }

            onClose();
        } catch (error) {
            if (error && error.response) {
                try {
                    let body = await error.response.json();
                    if (body && body.errors) {
                        let errors = {};
                        body.errors.forEach((e) => {
                            let key = e.key.charAt(0).toLowerCase() + e.key.slice(1);
                            errors[key] = Translate(e.value, e.type && e.type.values);
                        });
                        setFormErrors(errors);
                        return;
                    }
                } catch (e) {
                    // Could not parse response body
                }
            }
            setShowSaveError(true);
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
            }}
            component="form"
            ref={formRef}
            onSubmit={handlePublish}
            onKeyDown={preventEnterFromSubmittingForm}
        >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <SectionHeading>{labels.sectionContent}</SectionHeading>
                <Box>
                    <Typography
                        component="label"
                        htmlFor={FormNames.Title}
                        variant="subtitle1"
                        sx={{ display: 'block', mb: 1 }}
                    >
                        {labels.titleLabel}
                    </Typography>
                    <TextField
                        fullWidth
                        id={FormNames.Title}
                        name={FormNames.Title}
                        defaultValue={editingNews ? editingNews.title : ''}
                        error={Boolean(formErrors.title)}
                        helperText={formErrors.title || ''}
                        variant="outlined"
                        size="small"
                        onChange={() => clearError('title')}
                    />
                </Box>

                <Box sx={{ maxWidth: { sm: '50%' } }}>
                    <NewsDatePicker
                        name={FormNames.VisibleDate}
                        label={labels.visibleDatePicker}
                        value={visibleDate}
                        onChange={(newValue) => {
                            setVisibleDate(newValue);
                            setVisibleDateFollowsStartDate(newValue === null);
                        }}
                        error={formErrors.visibleDate}
                        hint={labels.visibleDateHint}
                        onEdit={() => clearError('visibleDate')}
                    />
                </Box>

                <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Typography id={contentLabelId} variant="subtitle1" sx={{ mb: 1 }}>
                            {labels.contentLabel}
                        </Typography>
                        <NewsEditorModalLinkButton
                            contentEditableRef={contentEditableRef}
                            onLinkInserted={handleContentChange}
                            labels={labels}
                        />
                    </Box>
                    <TextField
                        multiline
                        variant="outlined"
                        error={Boolean(formErrors.content)}
                        helperText={formErrors.content || labels.contentWidthHint}
                        InputProps={{
                            inputComponent: 'div' as any,
                            inputProps: {
                                ref: contentEditableRef,
                                contentEditable: true,
                                suppressContentEditableWarning: true,
                                role: 'textbox',
                                'aria-multiline': true,
                                'aria-labelledby': contentLabelId,
                                onInput: handleContentChange,
                                onPaste: (e: React.ClipboardEvent<HTMLElement>) => {
                                    pasteAsTextWithLinks(e);
                                    handleContentChange();
                                },
                                onClick: (e: React.MouseEvent) => {
                                    const target = (e.target as HTMLElement).closest('a');
                                    if (target && target.href) {
                                        e.preventDefault();
                                        window.open(target.href, '_blank');
                                    }
                                },
                                dangerouslySetInnerHTML: {
                                    __html: editingNews ? sanitizeHtml(editingNews.content) : ''
                                },
                                style: {
                                    width: `${newsTextWidthPx}px`,
                                    maxWidth: '100%',
                                    minHeight: '118px',
                                    outline: 'none',
                                    overflow: 'auto'
                                }
                            },
                            sx: {
                                '& .MuiInputBase-input': newsBodyTextSx,
                                '& a': { cursor: 'pointer' }
                            }
                        }}
                    />
                </Box>

                <Divider />

                <SectionHeading>{labels.sectionRecipients}</SectionHeading>
                <Box>
                    <Typography
                        component="label"
                        htmlFor={FormNames.Recipients}
                        variant="subtitle1"
                        sx={{ display: 'block', mb: 1 }}
                    >
                        {labels.recipientsLabel}
                    </Typography>
                    <Autocomplete
                        multiple
                        id={FormNames.Recipients}
                        options={roles}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={selectedRoles}
                        onChange={(_, newSelectedRoles: { label: string; value: UserRole }[]) => {
                            setSelectedRoles(newSelectedRoles);
                            clearError('recipients');

                            const brokerAgentSelected = newSelectedRoles.some(
                                (role) => role.value === UserRole.BrokerAgent
                            );
                            if (!brokerAgentSelected) {
                                setSelectedBrokerChains([]);
                                clearError('brokerChain');
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                name={FormNames.Recipients}
                                placeholder={labels.selectRecipientsPlaceholder}
                                error={Boolean(formErrors.recipients)}
                                helperText={formErrors.recipients || ''}
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option.value}
                                    label={option.label}
                                    sx={{
                                        zIndex: (theme) => theme.zIndex.modal + 1
                                    }}
                                />
                            ))
                        }
                    />
                </Box>

                {isBrokerAgentSelected && (
                    <Box>
                        <Typography
                            component="label"
                            htmlFor={FormNames.BrokerChain}
                            variant="subtitle1"
                            sx={{ display: 'block', mb: 1 }}
                        >
                            {labels.brokerChainsLabel}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allBrokerChainsSelected}
                                    indeterminate={
                                        selectedBrokerChains.length > 0 && !allBrokerChainsSelected
                                    }
                                    onChange={handleSelectAllBrokerChains}
                                    disabled={brokerChainsLoading || brokerChains.length === 0}
                                />
                            }
                            label={labels.brokerChainsSelectAllLabel}
                            sx={{ mb: 1 }}
                        />
                        <Autocomplete<{ id: number; name: string }, true, false, false>
                            multiple
                            id={FormNames.BrokerChain}
                            options={brokerChains}
                            loading={brokerChainsLoading}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedBrokerChains}
                            onChange={(_, newSelectedBrokerChains) => {
                                setSelectedBrokerChains(newSelectedBrokerChains);
                                clearError('brokerChain');
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={labels.brokerChainsPlaceholder}
                                    name={FormNames.BrokerChain}
                                    error={Boolean(formErrors.brokerChain)}
                                    helperText={formErrors.brokerChain || ''}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {brokerChainsLoading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.id}
                                        label={option.name}
                                        sx={{
                                            zIndex: (theme) => theme.zIndex.modal + 1
                                        }}
                                    />
                                ))
                            }
                        />
                    </Box>
                )}

                <Divider />

                <SectionHeading>{labels.sectionSchedule}</SectionHeading>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { sm: 'flex-start' },
                        gap: 2
                    }}
                >
                    <NewsDatePicker
                        name={FormNames.StartDate}
                        label={labels.startDatePicker}
                        value={startDate}
                        onChange={handleStartDateChange}
                        error={formErrors.startDate}
                        hint={labels.startDateHint}
                        onEdit={() => clearError('startDate')}
                    />
                    <Typography
                        aria-hidden
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            pt: 1,
                            color: 'text.secondary'
                        }}
                    >
                        →
                    </Typography>
                    <NewsDatePicker
                        name={FormNames.EndDate}
                        label={`${labels.endDatePicker} (${labels.optionalLabel})`}
                        value={endDate}
                        onChange={(newValue) => {
                            setEndDate(newValue);
                            refreshEndDateError(startDate, newValue);
                        }}
                        error={formErrors.endDate}
                        hint={labels.endDateHint}
                        onEdit={() => clearError('endDate')}
                    />
                </Box>
                <Alert severity={scheduleAudience.kind === 'shown' ? 'info' : 'warning'}>
                    {describeAudience(scheduleAudience).map((line) => (
                        <div key={line}>{line}</div>
                    ))}
                </Alert>
            </LocalizationProvider>

            <Divider sx={{ my: 1 }} />

            <Box display="flex" justifyContent="flex-end" gap={2}>
                {editingNews && (
                    <Button
                        variant="outlined"
                        size="medium"
                        name={FormNames.Action}
                        value={ButtonAction.Archive}
                        type="submit"
                        sx={{ textTransform: 'none', borderRadius: 0, mr: 'auto' }}
                    >
                        {editingNews.status === NewsItemStatus.Archived
                            ? labels.unarchiveButton
                            : labels.archiveButton}
                    </Button>
                )}
                <Button
                    variant="text"
                    size="medium"
                    type="button"
                    onClick={handlePreview}
                    sx={{ textTransform: 'none', borderRadius: 0 }}
                >
                    {labels.previewButton}
                </Button>
                <Button
                    variant="outlined"
                    size="medium"
                    name={FormNames.Action}
                    value={ButtonAction.SaveDraft}
                    type="submit"
                    sx={{ textTransform: 'none', borderRadius: 0 }}
                >
                    {labels.saveDraftButton}
                </Button>
                <Button
                    variant="contained"
                    size="medium"
                    color="primary"
                    type="submit"
                    name={FormNames.Action}
                    value={ButtonAction.Publish}
                    sx={{ textTransform: 'none', borderRadius: 0 }}
                >
                    {isScheduled ? labels.scheduleNewsButton : labels.publishNewsButton}
                </Button>
            </Box>
            <NewsPreviewDialog
                open={preview !== null}
                onClose={() => setPreview(null)}
                roles={roles}
                preview={preview}
            />
            <FailureSnackbar
                open={showSaveError}
                onClose={() => setShowSaveError(false)}
                text={Translate('news-editor/save-error')}
            />
        </Box>
    );
}
